<script setup>
import { i18n } from 'boot/i18n';

defineOptions({ name: 'ReviewManualAssignmentsTable' });

defineProps({
  rows: {
    type: Array,
    required: true,
  },
  cohortOptions: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits([
  'update-selection',
]);

const pagination = {
  rowsPerPage: 10,
};

const columns = [
  {
    name: 'clientName',
    label: i18n.t('settings.careModel.cohorts.review.client'),
    align: 'left',
    field: (row) => row.client?.clientName || '',
  },
  {
    name: 'matchingCohorts',
    label: i18n.t('settings.careModel.cohorts.review.matchingCohorts'),
    align: 'left',
    field: 'matchedCohorts',
    style: 'width: 360px; max-width: 360px;',
    headerStyle: 'width: 360px; max-width: 360px;',
  },
  {
    name: 'cohort',
    label: i18n.t('settings.careModel.cohorts.review.cohortName'),
    align: 'left',
    field: 'selectedCohortId',
    style: 'width: 320px; max-width: 320px;',
    headerStyle: 'width: 320px; max-width: 320px;',
  },
];

function getDisplayValue(selectedCohortId) {
  if (selectedCohortId) {
    return;
  }

  return i18n.t('settings.careModel.cohorts.review.selectCohort');
}

function formatMatchedLabels(matchedLabels = []) {
  return matchedLabels.join(', ');
}

function getMatchingCohort(row, cohortId) {
  const cohort = (row?.matchedCohorts || []).find((item) => item.id === cohortId);

  if (!cohort) {
    return null;
  }

  return {
    id: cohort.id,
    cohortName: cohort.cohortName,
    matchedLabels: row?.matchedLabelsByCohortId?.[cohort.id] || [],
  };
}

function getMatchingCohortsList(row) {
  return (row?.matchedCohorts || [])
    .map((cohort) => getMatchingCohort(row, cohort.id))
    .filter(Boolean);
}

function getMatchedCohortCaption(row, option) {
  const matchingCohort = getMatchingCohort(row, option?.value);

  if (!matchingCohort) {
    return '';
  }

  return formatMatchedLabels(matchingCohort.matchedLabels);
}
</script>

<template>
  <q-table
    :rows="rows"
    :columns="columns"
    row-key="client.id"
    flat
    :pagination="pagination"
    class="col-12 review-manual-assignments-table"
  >
    <template #body-cell-clientName="cellProps">
      <q-td :props="cellProps">
        {{ cellProps.row.client?.clientName }}
      </q-td>
    </template>

    <template #body-cell-matchingCohorts="cellProps">
      <q-td
        :props="cellProps"
        class="matching-cell"
      >
        <div
          v-if="getMatchingCohortsList(cellProps.row).length"
          class="matching-list column"
        >
          <div
            v-for="cohort in getMatchingCohortsList(cellProps.row)"
            :key="cohort.id"
            class="matching-list_item"
          >
            <span class="text-weight-medium">
              {{ cohort.cohortName }}
            </span>
            <span
              v-if="cohort.matchedLabels.length"
              class="text-grey-7"
            >
              : {{ formatMatchedLabels(cohort.matchedLabels) }}
            </span>
          </div>
        </div>
        <span
          v-else
          class="text-grey-6"
        >
          —
        </span>
      </q-td>
    </template>

    <template #body-cell-cohort="cellProps">
      <q-td
        :props="cellProps"
        class="cohort-cell"
      >
        <q-select
          class="cohort-select full-width"
          :model-value="cellProps.row.selectedCohortId"
          :options="cohortOptions"
          emit-value
          map-options
          clearable
          dense
          borderless
          :display-value="getDisplayValue(cellProps.row.selectedCohortId)"
          @update:model-value="emit('update-selection', cellProps.row.client.id, $event)"
        >
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label class="option-label">
                  {{ scope.opt.label }}
                </q-item-label>
                <q-item-label
                  v-if="getMatchedCohortCaption(cellProps.row, scope.opt)"
                  caption
                >
                  {{ getMatchedCohortCaption(cellProps.row, scope.opt) }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </q-select>
      </q-td>
    </template>
  </q-table>
</template>

<style scoped lang="scss">
.review-manual-assignments-table {
  .matching-cell {
    width: 360px;
    max-width: 360px;
  }

  .cohort-cell {
    width: 320px;
    max-width: 320px;
    vertical-align: top;
  }

  .matching-list {
    gap: 8px;

    &_item {
      white-space: normal;
      word-break: break-word;
      line-height: 1.4;
    }
  }

  .option-label {
    white-space: normal;
    word-break: break-word;
    line-height: 1.3;
  }

  :deep(.cohort-select .q-field__native),
  :deep(.cohort-select .q-field__input) {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    line-height: 1.3;
    min-width: 0;
  }

  :deep(.cohort-select .q-field__control) {
    min-height: 40px;
    height: auto;
    padding-right: 40px;
  }

  :deep(.cohort-select .q-field__marginal) {
    min-width: 40px;
    width: 40px;
    justify-content: center;
    align-self: flex-start;
    padding-top: 8px;
  }

  :deep(.cohort-select .q-field__append) {
    min-width: 40px;
    justify-content: center;
  }
}
</style>
